<template>
  <div>
    <form ref="form">
      <b-field :label="$t('legalName')">
        <b-input v-model="settings.legalName" required />
      </b-field>
      <b-field :label="$t('legalEmail')">
        <b-input v-model="settings.legalEmail" type="email" required />
      </b-field>
      <b-field :label="$t('telephoneNumber')" />
      <PhoneNumberPicker v-model="settings.telephoneNumber" :required="settings.mobileNumber ? false : true" />
      <b-field :label="$t('mobileNumber')" />
      <PhoneNumberPicker v-model="settings.mobileNumber" :required="settings.telephoneNumber ? false : true" />
      <b-field :label="$t('brandingPreview')" class="mt-6">
        <BrandingPreview
          :branding="{
            primaryColor: primaryColor,
            secondaryColor: secondaryColor,
            tertiaryColor: tertiaryColor,
            fourthColor: fourthColor,
            logoSrc: logoSrc,
          }"
        />
      </b-field>
      <b-field grouped group-multiline>
        <b-field :label="$t('primaryColor')">
          <chrome-picker v-model="primaryColor" :disable-alpha="true" />
        </b-field>
        <b-field :label="$t('secondaryColor')">
          <chrome-picker v-model="secondaryColor" :disable-alpha="true" />
        </b-field>
        <b-field :label="$t('tertiaryColor')">
          <chrome-picker v-model="tertiaryColor" :disable-alpha="true" />
        </b-field>
        <b-field :label="$t('fourthColor')">
          <chrome-picker v-model="fourthColor" :disable-alpha="true" />
        </b-field>
      </b-field>
      <b-field :label="$t('logoUpload')">
        <div style="max-width: 383px" class="card mb-4">
          <b-upload v-if="!logoSrc" v-model="logoFile" drag-drop>
            <section class="section card-content">
              <div class="content has-text-centered">
                <p>
                  <b-icon icon="upload" size="is-large" />
                </p>
                <p>{{ $t("logoUploadInstructions") }}</p>
                <p>{{ $t("allowedLogoFormat") }}</p>
              </div>
            </section>
          </b-upload>

          <div v-else class="card-content content">
            <i v-if="changed" class="ml-2">
              {{ $t("tempChanges") }}
            </i>
            <b-progress class="mt-2" :type="progress == 100 ? 'is-success' : ''" :value="progress" />
            <figure class="image">
              <img :src="logoSrc" class="" style="max-width: 300px" />
            </figure>
            <b-field class="has-text-centered">
              <b-button
                class="is-danger"
                @click="
                  logoFile = null;
                  logoSrc = null;
                  progress = 0;
                  checkValidity();
                "
              >
                {{ $t("delete") }}
              </b-button>
            </b-field>
          </div>
        </div>
      </b-field>
      <b-field :label="$t('legalStreetName')">
        <b-input v-model="settings.legalStreetName" required />
      </b-field>
      <b-field :label="$t('legalZipCode')">
        <b-input v-model="settings.legalZipCode" pattern="\d*" required />
      </b-field>
      <b-field :label="$t('legalCity')">
        <b-input v-model="settings.legalCity" required />
      </b-field>
    </form>
    <b-field class="mt-5" :grouped="true" :group-multiline="true">
      <b-button
        class="control"
        type="is-primary"
        icon-left="content-save-outline"
        :loading="loading"
        :disabled="!validity"
        @click="saveSettings"
      >
        {{ $t("saveSettings") }}
      </b-button>
      <b-button class="control" type="is-danger" icon-left="cancel" :loading="loading" @click="reload">
        {{ $t("cancel") }}
      </b-button>
    </b-field>
  </div>
</template>

<script>
import { db, storage } from "@/firebase";
import { Chrome } from "vue-color";
import PhoneNumberPicker from "@/components/PhoneNumberPicker";
import BrandingPreview from "@/components/BrandingPreview";

export default {
  name: "OrganizationSettings",
  components: {
    PhoneNumberPicker,
    "chrome-picker": Chrome,
    BrandingPreview,
  },
  props: {
    organizationId: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      loading: false,
      settings: {
        branding: {},
      },
      logoFile: null,
      progress: 0,
      changed: false,
      validity: true,
    };
  },
  computed: {
    organizationsCollection() {
      return db.collection("organizations");
    },
    logoSrc: {
      get() {
        return this.$store.getters.getBrandLogo;
      },
      set(src) {
        this.changed = true;
        this.$store.commit("CHANGE_LOGO", src);
      },
    },
    primaryColor: {
      get() {
        return this.$store.getters.getPrimaryColor;
      },
      set(color) {
        this.settings.branding.primaryColor = color.hex;
        this.$store.commit("CHANGE_COLOR_SCHEME", { primaryColor: color.hex });
      },
    },
    secondaryColor: {
      get() {
        return this.$store.getters.getSecondaryColor;
      },
      set(color) {
        this.settings.branding.secondaryColor = color.hex;
        this.$store.commit("CHANGE_COLOR_SCHEME", {
          secondaryColor: color.hex,
        });
      },
    },
    tertiaryColor: {
      get() {
        return this.$store.getters.getTertiaryColor;
      },
      set(color) {
        this.settings.branding.tertiaryColor = color.hex;
        this.$store.commit("CHANGE_COLOR_SCHEME", { tertiaryColor: color.hex });
      },
    },
    fourthColor: {
      get() {
        return this.$store.getters.getFourthColor;
      },
      set(color) {
        this.settings.branding.fourthColor = color.hex;
        this.$store.commit("CHANGE_COLOR_SCHEME", { fourthColor: color.hex });
      },
    },
  },
  watch: {
    settings: {
      deep: true,
      handler() {
        this.checkValidity();
      },
    },
    organizationId: {
      immediate: true,
      handler(organizationId) {
        this.$bind("settings", this.organizationsCollection.doc(organizationId));
        this.settings = {
          branding: {},
        };
      },
    },
    async logoFile(file) {
      let self = this;

      if (!file) {
        // onDelete do nothing
        return;
      }

      if (file.type.substring(0, 5) !== "image" || file.type == "image/tiff") {
        // check if image
        this.logoSrc = null;
        this.$buefy.toast.open({
          message: this.$t("noImageType"),
          type: "is-danger",
        });
        return;
      }

      let reader = new FileReader();
      reader.onload = function (event) {
        self.logoSrc = event.target.result;
        self.checkValidity();
      };

      reader.readAsDataURL(file);
    },
  },
  methods: {
    checkValidity() {
      let imagePresent = true;
      if (this.logoSrc == null) {
        imagePresent = false;
      }
      this.validity = this.$refs.form.checkValidity() && imagePresent;
    },
    async saveSettings() {
      this.loading = true;

      if (this.validity == true) {
        if (this.logoFile) {
          this.settings.branding.logoUrl = await this.uploadImage();
          this.changed = false;
        }

        this.organizationsCollection
          .doc(this.organizationId)
          .set(this.settings, { merge: true })
          .then(() => {
            this.$buefy.toast.open({
              message: this.$t("settingsSaved"),
              type: "is-success",
            });
            this.$store.commit("CHANGE_LOGO", this.settings.branding.logoUrl);
          })
          .catch((err) => {
            this.$buefy.toast.open({
              message: err,
              type: "is-danger",
            });
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    uploadImage() {
      let ref = storage
        .refFromURL("gs://travio-icons/")
        .child(`organizations/${this.organizationId}/${this.logoFile.name}`);
      let uploadTask = ref.put(this.logoFile);

      uploadTask.on(
        "state_changed",
        (snapshot) => {
          this.progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        (error) => {
          this.$buefy.toast.open({
            message: error,
            type: "is-danger",
          });
        },
      );
      return uploadTask.then(async (snapshot) => {
        return await snapshot.ref.getDownloadURL();
      });
    },
    reload() {
      location.reload();
    },
  },
};
</script>
